import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  conversationControllerListConversationsQueryOptions,
  conversationControllerListConversationsQueryKey,
  useConversationControllerDeleteConversation,
} from 'korint-sdk';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/spinner';
import { MessageSquarePlusIcon } from 'lucide-react';
import { ConversationThread } from './ConversationThread';
import { ConversationList } from './ConversationList';
import { NewConversationForm } from './NewConversationForm';
import { Center, Group, Stack } from '@folio/components/ui/flex';

export function ConversationModule({
  activeConversationId,
  onConversationSelect,
  onConversationDeselect,
}: {
  activeConversationId?: string;
  onConversationSelect?: (id: string) => void;
  onConversationDeselect?: () => void;
} = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(null);
  const [initialMessage, setInitialMessage] = useState<string | null>(null);

  // Use external routing props if provided, otherwise use internal state
  const selectedId = activeConversationId ?? internalSelectedId;
  const setSelectedId = useCallback(
    (id: string | null) => {
      if (id) {
        if (onConversationSelect) {
          onConversationSelect(id);
        } else {
          setInternalSelectedId(id);
        }
      } else {
        if (onConversationDeselect) {
          onConversationDeselect();
        } else {
          setInternalSelectedId(null);
        }
      }
    },
    [onConversationSelect, onConversationDeselect],
  );

  const { data: conversationsList, isLoading } = useQuery({
    ...conversationControllerListConversationsQueryOptions(),
  });

  const { mutateAsync: deleteConversation, isPending: isDeleting } =
    useConversationControllerDeleteConversation({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: conversationControllerListConversationsQueryKey(),
          });
          setSelectedId(null);
        },
      },
    });

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteConversation({ conversationId: id });
    },
    [deleteConversation],
  );

  const handleNewConversationCreated = useCallback(
    (conversationId: string, message: string) => {
      setInitialMessage(message);
      setSelectedId(conversationId);
    },
    [setSelectedId],
  );

  return (
    <Group
      align="stretch"
      gap={0}
      className="rounded-lg border bg-card text-card-foreground shadow-sm h-[calc(100dvh-2.5rem)]"
    >
      {/* Sidebar */}
      <div className="hidden md:flex w-72 border-r flex-col shrink-0">
        <div className="p-2">
          <Button
            onClick={() => setSelectedId(null)}
            variant="outline"
            className="w-full justify-start gap-2"
          >
            <MessageSquarePlusIcon className="h-4 w-4" />
            {t('conversation.newConversation')}
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <Center className="py-8">
              <Spinner className="h-5 w-5" />
            </Center>
          ) : (
            <ConversationList
              conversations={conversationsList?.conversations ?? []}
              activeId={selectedId ?? undefined}
              onSelect={setSelectedId}
              onDelete={handleDelete}
              isDeleting={isDeleting}
            />
          )}
        </div>
      </div>

      {/* Main area */}
      <Stack gap={0} className="flex-1 min-w-0">
        {selectedId ? (
          <ConversationThread
            key={selectedId}
            conversationId={selectedId}
            initialMessage={initialMessage}
            onInitialMessageSent={() => setInitialMessage(null)}
            onNavigate={path => navigate(path)}
            source="tab"
          />
        ) : (
          <NewConversationForm onConversationCreated={handleNewConversationCreated} source="tab" />
        )}
      </Stack>
    </Group>
  );
}
